'use client'

import React from 'react'
import {
  TrendingUp,
  Search,
  Bot,
  MousePointerClick,
  Eye,
  BarChart3,
  Globe,
  ArrowRight,
  Newspaper,
  ShoppingCart,
  GraduationCap,
  Building2,
  Sparkles,
} from 'lucide-react'

/**
 * AI Search Shift Diagrams
 * Visual components for the "Google Traffic Dropped 33%" blog post
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A, Purple #8b5cf6
 */

/**
 * Zero-Click Rate Progression
 */
export function ZeroClickProgressionDiagram() {
  const data = [
    { year: '2020', rate: 50, label: '~50%' },
    { year: '2022', rate: 55, label: '~55%' },
    { year: '2024', rate: 60, label: '~60%' },
    { year: '2025', rate: 69, label: '69%' },
    { year: 'AI Queries', rate: 83, label: '83%', highlight: true },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'>
        <div className='mb-6 flex items-center gap-2'>
          <MousePointerClick className='h-5 w-5 text-[#F90B8A]' />
          <span className='text-lg font-semibold text-foreground'>
            Zero-Click Search Rate Over Time
          </span>
        </div>
        <div className='flex items-end justify-between gap-3'>
          {data.map(item => (
            <div
              key={item.year}
              className='flex flex-1 flex-col items-center gap-2'
            >
              <span
                className='text-sm font-bold'
                style={{ color: item.highlight ? '#F90B8A' : '#008dcc' }}
              >
                {item.label}
              </span>
              <div
                className='w-full overflow-hidden rounded-t-lg bg-muted'
                style={{ height: '160px' }}
              >
                <div
                  className='w-full rounded-t-lg transition-all duration-500'
                  style={{
                    height: `${(item.rate / 100) * 160}px`,
                    marginTop: `${160 - (item.rate / 100) * 160}px`,
                    background: item.highlight
                      ? 'linear-gradient(180deg, #F90B8A, #d4066e)'
                      : 'linear-gradient(180deg, #008dcc, #006a9e)',
                  }}
                />
              </div>
              <span className='text-xs font-medium text-muted-foreground'>
                {item.year}
              </span>
            </div>
          ))}
        </div>
        <p className='mt-4 text-center text-xs text-muted-foreground'>
          Sources: Semrush 2025, Similarweb, Click-Vision
        </p>
      </div>
    </div>
  )
}

/**
 * AI Traffic Conversion Comparison
 */
export function ConversionComparisonDiagram() {
  const platforms = [
    { name: 'Google Organic', rate: 2.8, color: '#6b7280', icon: Search },
    { name: 'Google Ads', rate: 3.7, color: '#9ca3af', icon: Search },
    { name: 'Claude', rate: 5.0, color: '#8b5cf6', icon: Bot },
    { name: 'Perplexity', rate: 10.5, color: '#FDC304', icon: Bot },
    { name: 'AI Average', rate: 14.2, color: '#008dcc', icon: Sparkles },
    { name: 'ChatGPT', rate: 15.9, color: '#10b981', icon: Bot },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'>
        <div className='mb-6 flex items-center gap-2'>
          <TrendingUp className='h-5 w-5 text-[#008dcc]' />
          <span className='text-lg font-semibold text-foreground'>
            Conversion Rate by Traffic Source
          </span>
        </div>
        <div className='space-y-3'>
          {platforms.map(platform => {
            const Icon = platform.icon
            return (
              <div key={platform.name} className='flex items-center gap-3'>
                <div className='flex w-28 shrink-0 items-center gap-1.5'>
                  <Icon
                    className='h-3.5 w-3.5'
                    style={{ color: platform.color }}
                  />
                  <span className='text-xs font-medium text-foreground/80'>
                    {platform.name}
                  </span>
                </div>
                <div className='flex-1'>
                  <div className='h-7 w-full rounded-lg bg-muted'>
                    <div
                      className='flex h-7 items-center justify-end rounded-lg px-2 transition-all duration-500'
                      style={{
                        width: `${(platform.rate / 16) * 100}%`,
                        backgroundColor: platform.color,
                        minWidth: '40px',
                      }}
                    >
                      <span className='text-xs font-bold text-white'>
                        {platform.rate}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
        <div className='mt-4 rounded-lg bg-[#008dcc]/5 border border-[#008dcc]/20 p-3'>
          <p className='text-xs text-muted-foreground'>
            <strong>Key insight:</strong> ChatGPT traffic converts at 5.7x the
            rate of Google organic search. Lower volume, dramatically higher
            quality.
          </p>
        </div>
        <p className='mt-3 text-center text-xs text-muted-foreground'>
          Source: Superprompt analysis of 12M website visits, 2025
        </p>
      </div>
    </div>
  )
}

/**
 * AI Search Platform Market Share
 */
export function AISearchMarketShareDiagram() {
  const platforms = [
    {
      name: 'ChatGPT',
      share: 84.2,
      referrals: '84.2% of AI referrals',
      color: '#10b981',
      growth: '3.26x YoY',
    },
    {
      name: 'Perplexity',
      share: 5.8,
      referrals: '30M daily queries',
      color: '#FDC304',
      growth: '370% YoY',
    },
    {
      name: 'Gemini',
      share: 4.2,
      referrals: '400M monthly users',
      color: '#008dcc',
      growth: '237% YoY',
    },
    {
      name: 'Copilot',
      share: 3.1,
      referrals: '33M active users',
      color: '#8b5cf6',
      growth: '25.2x YoY',
    },
    {
      name: 'Claude',
      share: 2.7,
      referrals: 'Growing fast',
      color: '#F90B8A',
      growth: '12.8x YoY',
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'>
        <div className='mb-6 flex items-center gap-2'>
          <Globe className='h-5 w-5 text-[#008dcc]' />
          <span className='text-lg font-semibold text-foreground'>
            AI Search Platform Landscape (2025)
          </span>
        </div>
        <div className='space-y-4'>
          {platforms.map(platform => (
            <div
              key={platform.name}
              className='rounded-lg border border-border/60 p-3'
            >
              <div className='mb-2 flex items-center justify-between'>
                <div className='flex items-center gap-2'>
                  <div
                    className='h-3 w-3 rounded-full'
                    style={{ backgroundColor: platform.color }}
                  />
                  <span className='text-sm font-semibold text-foreground'>
                    {platform.name}
                  </span>
                </div>
                <span
                  className='rounded-full px-2 py-0.5 text-xs font-bold text-white'
                  style={{ backgroundColor: platform.color }}
                >
                  {platform.growth}
                </span>
              </div>
              <div className='mb-1 h-2 w-full overflow-hidden rounded-full bg-muted'>
                <div
                  className='h-2 rounded-full transition-all duration-500'
                  style={{
                    width: `${Math.max(platform.share, 3)}%`,
                    backgroundColor: platform.color,
                  }}
                />
              </div>
              <p className='text-xs text-muted-foreground'>
                {platform.referrals}
              </p>
            </div>
          ))}
        </div>
        <p className='mt-4 text-center text-xs text-muted-foreground'>
          Sources: Previsible (1.96M LLM sessions), Similarweb, DemandSage
        </p>
      </div>
    </div>
  )
}

/**
 * Industry Impact Breakdown
 */
export function IndustryImpactDiagram() {
  const industries = [
    {
      name: 'Publishers / Media',
      impact: 'Severe',
      stat: 'Traffic down 33%',
      detail: 'Google Web Search referrals to news dropped from 51% to 27%',
      color: '#ef4444',
      icon: Newspaper,
    },
    {
      name: 'Education / Reference',
      impact: 'Severe',
      stat: 'Chegg down 49%',
      detail: 'Q&A content directly displaced by AI answers',
      color: '#ef4444',
      icon: GraduationCap,
    },
    {
      name: 'B2B / SaaS',
      impact: 'Mixed',
      stat: 'High-quality AI leads',
      detail: '86% of AI hand-raisers are high intent (requesting demos)',
      color: '#FDC304',
      icon: Building2,
    },
    {
      name: 'E-commerce',
      impact: 'Moderate',
      stat: 'Google still wins on revenue',
      detail: 'AI traffic growing 805% YoY for retail but from small base',
      color: '#008dcc',
      icon: ShoppingCart,
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'>
        <div className='mb-6 flex items-center gap-2'>
          <BarChart3 className='h-5 w-5 text-[#8b5cf6]' />
          <span className='text-lg font-semibold text-foreground'>
            Industry Impact: Who Gets Hit Hardest
          </span>
        </div>
        <div className='space-y-3'>
          {industries.map(industry => {
            const Icon = industry.icon
            return (
              <div
                key={industry.name}
                className='rounded-lg border p-4'
                style={{ borderColor: `${industry.color}30` }}
              >
                <div className='mb-2 flex items-center justify-between'>
                  <div className='flex items-center gap-2'>
                    <Icon
                      className='h-4 w-4'
                      style={{ color: industry.color }}
                    />
                    <span className='text-sm font-semibold text-foreground'>
                      {industry.name}
                    </span>
                  </div>
                  <span
                    className='rounded-full px-2 py-0.5 text-xs font-bold'
                    style={{
                      backgroundColor: `${industry.color}15`,
                      color: industry.color,
                    }}
                  >
                    {industry.impact}
                  </span>
                </div>
                <p
                  className='text-sm font-medium'
                  style={{ color: industry.color }}
                >
                  {industry.stat}
                </p>
                <p className='mt-1 text-xs text-muted-foreground'>
                  {industry.detail}
                </p>
              </div>
            )
          })}
        </div>
        <p className='mt-4 text-center text-xs text-muted-foreground'>
          Sources: Press Gazette, HockeyStack, Adobe, Previsible
        </p>
      </div>
    </div>
  )
}

/**
 * Discovery vs Selection Diagram
 */
export function DiscoveryVsSelectionDiagram() {
  return (
    <div data-blog-layout='narrow'>
      <div className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'>
        <div className='mb-6 flex items-center gap-2'>
          <Eye className='h-5 w-5 text-[#FDC304]' />
          <span className='text-lg font-semibold text-foreground'>
            The Fundamental Shift
          </span>
        </div>
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
          {/* Traditional SEO */}
          <div className='rounded-lg border-2 border-border/60 bg-muted p-4'>
            <div className='mb-3 flex items-center gap-2'>
              <Search className='h-5 w-5 text-muted-foreground' />
              <span className='text-sm font-bold text-foreground/80'>
                Traditional SEO
              </span>
            </div>
            <p className='mb-3 text-2xl font-black text-muted-foreground'>
              Discovery
            </p>
            <ul className='space-y-2 text-xs text-muted-foreground'>
              <li className='flex items-start gap-1.5'>
                <ArrowRight className='mt-0.5 h-3 w-3 shrink-0 text-muted-foreground' />
                <span>Rank in a list of 10 blue links</span>
              </li>
              <li className='flex items-start gap-1.5'>
                <ArrowRight className='mt-0.5 h-3 w-3 shrink-0 text-muted-foreground' />
                <span>Win by keywords and backlinks</span>
              </li>
              <li className='flex items-start gap-1.5'>
                <ArrowRight className='mt-0.5 h-3 w-3 shrink-0 text-muted-foreground' />
                <span>User clicks through to your site</span>
              </li>
              <li className='flex items-start gap-1.5'>
                <ArrowRight className='mt-0.5 h-3 w-3 shrink-0 text-muted-foreground' />
                <span>Measure: rankings, traffic, CTR</span>
              </li>
            </ul>
          </div>

          {/* AI Search / GEO */}
          <div className='rounded-lg border-2 border-[#008dcc] bg-[#008dcc]/5 p-4'>
            <div className='mb-3 flex items-center gap-2'>
              <Bot className='h-5 w-5 text-[#008dcc]' />
              <span className='text-sm font-bold text-[#008dcc]'>
                AI Search (GEO)
              </span>
            </div>
            <p className='mb-3 text-2xl font-black text-[#008dcc]'>Selection</p>
            <ul className='space-y-2 text-xs text-muted-foreground'>
              <li className='flex items-start gap-1.5'>
                <ArrowRight className='mt-0.5 h-3 w-3 shrink-0 text-[#008dcc]' />
                <span>Get cited in a synthesized answer</span>
              </li>
              <li className='flex items-start gap-1.5'>
                <ArrowRight className='mt-0.5 h-3 w-3 shrink-0 text-[#008dcc]' />
                <span>Win by brand authority and content clarity</span>
              </li>
              <li className='flex items-start gap-1.5'>
                <ArrowRight className='mt-0.5 h-3 w-3 shrink-0 text-[#008dcc]' />
                <span>User may never visit your site</span>
              </li>
              <li className='flex items-start gap-1.5'>
                <ArrowRight className='mt-0.5 h-3 w-3 shrink-0 text-[#008dcc]' />
                <span>Measure: citations, brand mentions, share of voice</span>
              </li>
            </ul>
          </div>
        </div>
        <div className='mt-4 rounded-lg bg-[#FDC304]/5 border border-[#FDC304]/20 p-3'>
          <p className='text-center text-xs font-medium text-muted-foreground'>
            Only ~50% overlap between Google #1 rankings and AI search
            visibility (Semrush)
          </p>
        </div>
      </div>
    </div>
  )
}
